import {
  $isReferenceCitationNode,
  ReferenceCitationNode,
} from '../../../nodes/ReferenceCitationNode';
import {
  $isReferenceCitationSeparatorNode,
  ReferenceCitationSeparatorNode,
} from '../../../nodes/ReferenceCitationSeparatorNode';

type ReferencesNode = ReferenceCitationNode | ReferenceCitationSeparatorNode;

function getNumber(
  node: ReferenceCitationNode | null,
  numbersMap: Record<string, number>,
): number {
  if (!node) {
    return -1;
  }

  return numbersMap[node.getReferenceId()] ?? -1;
}

function getNextReference(node: ReferenceCitationNode): ReferenceCitationNode | null {
  let next = node.getNextSibling();
  if ($isReferenceCitationNode(next)) {
    return next;
  }
  if (!$isReferenceCitationSeparatorNode(next)) {
    return null;
  }
  next = next.getNextSibling();

  return $isReferenceCitationNode(next) ? next : null;
}

function getPreviousReference(node: ReferenceCitationNode): ReferenceCitationNode | null {
  let prev = node.getPreviousSibling();
  if ($isReferenceCitationNode(prev)) {
    return prev;
  }
  if (!$isReferenceCitationSeparatorNode(prev)) {
    return null;
  }
  prev = prev.getPreviousSibling();

  return $isReferenceCitationNode(prev) ? prev : null;
}

function getNextSeparator(node: ReferenceCitationNode): ReferenceCitationSeparatorNode | null {
  const next = node.getNextSibling();

  return $isReferenceCitationSeparatorNode(next) ? next : null;
}

export function $setNodeText(
  node: ReferenceCitationNode,
  numbersMap: Record<string, number>,
): ReferencesNode[] {
  node.setNumber(getNumber(node, numbersMap));
  const separator = getNextSeparator(node);
  if (!separator) {
    return [node];
  }

  return [node, separator.convertToComma()];
}

export function $setNodeTextWithNumbersRange(
  node: ReferenceCitationNode,
  numbersMap: Record<string, number>,
): ReferencesNode[] {
  const current = getNumber(node, numbersMap);
  const nextReference = getNextReference(node);

  // current citation is the last number in the list
  if (!nextReference) {
    node.setNumber(current);

    return [node];
  }

  const separator = getNextSeparator(node);
  const prev = getNumber(getPreviousReference(node), numbersMap);
  const next = getNumber(nextReference, numbersMap);
  const afterNext = getNumber(getNextReference(nextReference), numbersMap);
  const mutadedNode = separator ? [node, separator] : [node];

  // current citation number or next one is missing, the actual position in the list doesn't matter
  if (current <= 0 || next <= 0) {
    node.setNumber(current);
    separator?.convertToComma();

    return mutadedNode;
  }

  // current citatation is in the middle of the list
  if (prev === current - 1) {
    if (next === current + 1) {
      node.setHidden();
      separator?.convertToHidden();
    } else {
      node.setNumber(current);
      separator?.convertToComma();
    }

    return mutadedNode;
  }

  // check if the current ciration is a begging of the consequent list
  if (next === current + 1 && afterNext === current + 2) {
    node.setNumber(current);
    separator?.convertToDash();

    return mutadedNode;
  }

  node.setNumber(current);
  separator?.convertToComma();

  return mutadedNode;
}
